import { useState } from "react";
import { Pencil, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { adminApi, formatApiError } from "@/lib/adminAuth";
import { EtapaIcon } from "@/components/rastreo/EtapaIcon";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { ADMIN } from "@/constants/testIds";
import { usePageTitle } from "@/lib/usePageTitle";
import { ErrorState } from "@/components/common/ErrorState";

export default function Etapas() {
  usePageTitle("Etapas");
  const queryClient = useQueryClient();
  const [dialogo, setDialogo] = useState(null); // { etapa }
  const [confirmarDesactivar, setConfirmarDesactivar] = useState(null); // etapa a desactivar
  const [guardandoActiva, setGuardandoActiva] = useState(null); // id de la etapa en vuelo

  const { data: etapas, isLoading, isError, refetch } = useQuery({
    queryKey: ["etapas"],
    queryFn: () => adminApi.get("/admin/etapas").then((r) => r.data),
  });

  // Fix A (2026-09-01): activa/desactiva sin tocar orden/cantidad. Misma mecánica que el
  // guardado de nombre/descripción (PUT con todo el body, toast, invalidación de la query).
  const guardarActiva = async (etapa, nuevoValor) => {
    setGuardandoActiva(etapa.id);
    try {
      await adminApi.put(`/admin/etapas/${etapa.id}`, {
        nombre: etapa.nombre, descripcion_corta: etapa.descripcion_corta, activa: nuevoValor,
      });
      toast.success(nuevoValor ? "Etapa activada" : "Etapa desactivada");
      // Activar/desactivar afecta el timeline, el dashboard, la lista y las plantillas:
      // invalidamos todo lo que muestra etapas para que el cambio se vea sin recargar.
      queryClient.invalidateQueries({ queryKey: ["etapas"] });
      queryClient.invalidateQueries({ queryKey: ["admin-dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unidades"] });
      queryClient.invalidateQueries({ queryKey: ["admin-unidad"] });
      queryClient.invalidateQueries({ queryKey: ["admin-plantillas"] });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setGuardandoActiva(null);
      setConfirmarDesactivar(null);
    }
  };

  const toggleActiva = (etapa) => {
    if (etapa.activa === false) {
      guardarActiva(etapa, true);
    } else {
      setConfirmarDesactivar(etapa);
    }
  };

  return (
    <div className="mx-auto max-w-3xl px-8 py-8">
      <h1 className="text-xl font-semibold text-[var(--tc-text)]">Etapas</h1>
      <p className="mt-1 text-sm text-[var(--tc-text-secondary)]">
        Son 7 etapas fijas (no se pueden agregar, quitar ni reordenar). Podés renombrarlas,
        cambiar su descripción y activarlas o desactivarlas según cómo trabaje cada
        operación: el cambio se refleja de inmediato en todas las pantallas, incluidas las
        unidades ya cargadas.
      </p>

      <div className="mt-6 overflow-hidden rounded-[var(--tc-radius)] border border-[var(--tc-border)] bg-white">
        {isLoading ? (
          <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-[var(--tc-accent)]" size={22} /></div>
        ) : isError ? (
          <ErrorState testId="admin-etapas-error" mensaje="No se pudieron cargar las etapas." onRetry={refetch} />
        ) : (
          <div className="divide-y divide-[var(--tc-border)]">
            {etapas.map((etapa) => (
              <div key={etapa.id} data-testid={ADMIN.etapasRow} className="flex items-start justify-between gap-4 p-5">
                <div className="flex items-start gap-3">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[var(--tc-radius)] bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]">
                    <EtapaIcon nombre={etapa.icono} size={16} />
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-[var(--tc-text)]">{etapa.orden}. {etapa.nombre}</p>
                    <p className="mt-1 max-w-md text-xs leading-relaxed text-[var(--tc-text-secondary)]">{etapa.descripcion_corta}</p>
                  </div>
                </div>
                {/* Fix A (2026-09-01): interruptor de activa/inactiva. Una etapa desactivada
                    desaparece del recorrido, del dashboard y del avance, pero conserva sus
                    eventos históricos en la base. */}
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    data-testid={ADMIN.etapasToggleButton}
                    onClick={() => toggleActiva(etapa)}
                    disabled={guardandoActiva === etapa.id}
                    className={`rounded-full px-3 py-1 text-xs font-medium disabled:opacity-60 ${etapa.activa === false ? "bg-[var(--tc-surface)] text-[var(--tc-text-secondary)]" : "bg-[var(--tc-accent-soft)] text-[var(--tc-accent)]"}`}
                  >
                    {etapa.activa === false ? "Inactiva" : "Activa"}
                  </button>
                  <button
                    data-testid={ADMIN.etapasEditButton}
                    onClick={() => setDialogo({ etapa })}
                    className="shrink-0 rounded-[var(--tc-radius)] p-1.5 text-[var(--tc-text-secondary)] hover:bg-[var(--tc-surface)] hover:text-[var(--tc-accent)]"
                  >
                    <Pencil size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <AlertDialog open={Boolean(confirmarDesactivar)} onOpenChange={(v) => !v && setConfirmarDesactivar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Desactivar "{confirmarDesactivar?.nombre}"?</AlertDialogTitle>
            <AlertDialogDescription>
              Al desactivar esta etapa deja de aparecer en el recorrido de todas las unidades. Los eventos ya registrados se conservan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={ADMIN.confirmDialogCancel}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              data-testid={ADMIN.confirmDialogConfirm}
              disabled={guardandoActiva === confirmarDesactivar?.id}
              onClick={() => guardarActiva(confirmarDesactivar, false)}
            >
              Desactivar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {dialogo && (
        <EtapaDialog
          etapa={dialogo.etapa}
          onClose={() => setDialogo(null)}
          onSaved={() => { setDialogo(null); queryClient.invalidateQueries({ queryKey: ["etapas"] }); }}
        />
      )}
    </div>
  );
}

function EtapaDialog({ etapa, onClose, onSaved }) {
  const [nombre, setNombre] = useState(etapa.nombre);
  const [descripcion, setDescripcion] = useState(etapa.descripcion_corta);
  const [error, setError] = useState("");
  const [guardando, setGuardando] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setGuardando(true);
    try {
      await adminApi.put(`/admin/etapas/${etapa.id}`, { nombre, descripcion_corta: descripcion });
      toast.success("Etapa guardada");
      onSaved();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Etapa {etapa.orden}</DialogTitle>
          <DialogDescription>El orden no se puede cambiar acá; solo nombre y descripción.</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Nombre</label>
            <input
              data-testid={ADMIN.etapaFormNombre}
              required value={nombre} onChange={(e) => setNombre(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
          <div>
            <label className="mb-1.5 block text-sm font-medium text-[var(--tc-text)]">Descripción corta</label>
            <textarea
              data-testid={ADMIN.etapaFormDescripcion}
              required rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)}
              className="w-full rounded-[var(--tc-radius)] border border-[var(--tc-border)] px-3 py-2 text-sm outline-none focus:border-[var(--tc-accent)]"
            />
          </div>
          {error && <p className="rounded-[var(--tc-radius)] bg-red-50 px-3 py-2 text-sm text-red-600">{error}</p>}
          <DialogFooter>
            <button data-testid={ADMIN.etapaFormSubmit} type="submit" disabled={guardando} className="inline-flex items-center gap-2 rounded-[var(--tc-radius)] bg-[var(--tc-accent)] px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
              {guardando && <Loader2 size={14} className="animate-spin" />} Guardar
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
